import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../store/store';
import type { DayEntry, DayOverview } from '../slices/overviewSlice';
import {
  planDays,
  unplanDays,
  fetchMonthlyOverview,
} from '../slices/overviewSlice';
import { STATUS_META } from './MonthGrid';

const TYPE_LABELS: Record<string, string> = {
  work: 'Arbeit',
  sick: 'Krank',
  vacation: 'Urlaub',
};

const toTimeInput = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const entryHours = (entry: DayEntry) => {
  if (!entry.endTime) return 0;
  const ms = new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime();
  const pauseMin = entry.type === 'work' ? (entry.pausedDuration || 0) / 60 : 0;
  return Math.max(0, Math.round((ms / 3600000 - pauseMin / 60) * 100) / 100);
};

interface Props {
  day: DayOverview;
  month: string;
  userId?: string;
  isAdmin?: boolean;
  onClose: () => void;
}

const DayEditor: React.FC<Props> = ({ day, month, userId, isAdmin, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const effectiveStatus = !isAdmin && day.status === 'missing' ? 'future' : day.status;
  const meta = STATUS_META[effectiveStatus];

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const canPlan = day.date >= tomorrow.toLocaleDateString('sv-SE');

  const prettyDate = useMemo(() => {
    const [y, m, d] = day.date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }, [day.date]);

  const run = async (action: () => Promise<any>) => {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (e: any) {
      setError(e?.message || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  const handlePlan = () =>
    run(async () => {
      if (!canPlan) return;
      await dispatch(planDays({ dates: [day.date], userId })).unwrap();
      await dispatch(fetchMonthlyOverview({ month, userId }));
    });

  const handleUnplan = () =>
    run(async () => {
      await dispatch(unplanDays({ dates: [day.date], userId })).unwrap();
      await dispatch(fetchMonthlyOverview({ month, userId }));
    });

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5 className="mb-1" style={{ textTransform: 'capitalize' }}>
              {prettyDate}
            </h5>
            <span
              className="badge"
              style={{ background: meta.bg, color: meta.color }}
            >
              {day.status === 'missing' && !isAdmin ? 'Kein Eintrag' : (meta.label || 'Zukunft')}
            </span>
            {day.soll > 0 && (
              <span className="text-muted ms-2" style={{ fontSize: 13 }}>
                {day.ist} / {day.soll}h
              </span>
            )}
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Schließen" />
        </div>

        {day.status === 'holiday' && (
          <p className="text-muted mb-2">Feiertag{day.holidayName ? `: ${day.holidayName}` : ''}</p>
        )}

        {error && <div className="alert alert-danger py-2">{error}</div>}

        {/* Einträge des Tags */}
        {day.entries.length > 0 && (
          <ul className="list-group list-group-flush mb-3">
            {day.entries.map((entry) => (
              <li
                key={entry._id}
                className="list-group-item d-flex justify-content-between align-items-center px-0"
              >
                <span>
                  <span className="badge bg-light text-dark me-2">
                    {TYPE_LABELS[entry.type] || entry.type}
                  </span>
                  {toTimeInput(entry.startTime)}
                  {' – '}
                  {toTimeInput(entry.endTime) || '…'}
                  <span className="text-muted ms-2">({entryHours(entry)}h)</span>
                </span>
              </li>
            ))}
          </ul>
        )}

        {day.date <= new Date().toLocaleDateString('sv-SE') && day.status === 'missing' && (
          <div className="alert alert-secondary py-2" style={{ fontSize: 13 }}>
            Dieser fehlende Arbeitstag ist gesperrt. Bitte nutze dafür einen Zeitänderungsantrag.
          </div>
        )}

        {/* Planung nur fuer zukuenftige Tage */}
        {canPlan && day.status !== 'vacation' && day.status !== 'holiday' && day.status !== 'sick' && day.status !== 'off' && (
          <div className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
            <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Tagesplanung</div>
            {day.planned ? (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="text-muted" style={{ fontSize: 13 }}>Anwesenheit geplant</span>
                <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={handleUnplan}>
                  Entfernen
                </button>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-sm btn-primary" disabled={busy} onClick={handlePlan}>
                  Anwesenheit planen
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DayEditor;
